import React from 'react';
import autoBind from 'react-autobind';
import Config from "../Config.jsx";
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, TextField, CircularProgress, Box
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import Localization from "../Localization.jsx";
import { localeQuotaNoticeText } from "../tools/localeQuotaNotice.js";
import { serverErrorKey, failureRemark } from "../tools/serverErrorKey.js";

const styles = {
    pageContainer: {
        padding: '20px',
    },
    sectionTitle: {
        marginBottom: '16px',
        marginTop: '20px',
    },
    tableContainer: {
        marginBottom: '32px',
    },
    placeholder: {
        textAlign: 'center',
        padding: '20px',
        color: '#888',
    },
    thumbnail: {
        height: '80px',
        width: '120px',
        objectFit: 'cover',
        borderRadius: '4px',
    }
};

class ResourceLibraryPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            myEpacks: [],
            schoolEpacks: [],
            otherEpacks: [],
            isLoading: true,
            isDuplicating: false,
            // Remaining locale quota as get_epack_quota answered it, UNPARSED.
            // Read only by the red notice; null means "no honest figure".
            epackQuota: null,
            dialogOpen: false,
            selectedEpack: null,
            newEpackName: '',
        };
        autoBind(this);
    }

    componentDidMount() {
        this.fetchDuplicatableEpacks();
    }

    fetchDuplicatableEpacks() {
        this.setState({ isLoading: true });
        const userId = this.props.root.getMyUserId();
        if (!userId) {
            this.props.root.addNotification(
                Localization.strings.SESSION_EXPIRED || "Session expired. Please sign in again.",
                false,
            );
            this.setState({ isLoading: false });
            return;
        }
        const requestObj = { user_id: userId };
        this.props.root.refs.API.request(
            "get_epack_list_allows_duplicate",
            requestObj,
            this.fetchSuccess,
            this.fetchFail
        );
    }

    fetchSuccess(res) {
        // On the OK path res.body.result may be null/non-object (see API.jsx contract);
        // destructuring null throws inside the superagent callback and the page would be
        // stuck on its spinner forever, so coalesce to {} first.
        const result = (res && res.body && res.body.result && typeof res.body.result === 'object') ? res.body.result : {};
        const { my_epacks, school_epacks, other_epacks } = result;
        this.setState({
            myEpacks: my_epacks || [],
            schoolEpacks: school_epacks || [],
            otherEpacks: other_epacks || [],
            isLoading: false,
        });
    }

    fetchFail(err) {
        const key = serverErrorKey(err);
        console.error("Failed to fetch duplicatable epacks:", failureRemark(err) || err);
        this.props.root.addNotification(
            (key && Localization.strings[key])
                || Localization.strings.RESOURCE_LIBRARY_LOAD_FAILED
                || "Failed to load the resource library. Please try again.",
            false,
        );
        this.setState({ isLoading: false });
    }

    handleDuplicateClick(epack) {
        // Duplicating MAKES a new locale and spends a slot, so the same rule
        // applies: a school with no subscription in force is entitled to 0 and
        // cannot create one. Ahead of the DUPLICATE_LOCALE privilege on purpose —
        // that privilege arrives at its catalog default for a lapsed school, and
        // the quota call below answers 99999 for one that never subscribed, so
        // neither of the two checks that follow would stop it.
        if (!this.props.root.canCreateLocale()) {
            this.props.root.showSubscriptionRequiredDialog(
                Localization.strings.LOCALE_CREATE_BLOCKED_TITLE,
                Localization.strings.LOCALE_CREATE_BLOCKED_MESSAGE,
            );
            return;
        }

        // Gate duplication by subscription flag and DUPLICATE_LOCALE privilege
        const enforceSubscription = (typeof Config.SUBSCRIPTION_FLAG === 'function' && Config.SUBSCRIPTION_FLAG() === true);
        try {
            if (enforceSubscription) {
                const privilege = this.props.root.getPrivilege('DUPLICATE_LOCALE');
                const allowed = privilege && (String(privilege.value) === '1' || String(privilege.value).toLowerCase() === 'true');
                if (!allowed) {
                    // Show dialog like storage-limit flow
                    const link = (typeof Config.SUBSCRIPTION_MANAGE_URL === 'function')
                        ? Config.SUBSCRIPTION_MANAGE_URL()
                        : (window.location.origin + '/');
                    if (this.props.root && this.props.root.refs && this.props.root.refs.confirmDialog) {
                        this.props.root.refs.confirmDialog.show(
                            {
                                title: Localization.strings.DUPLICATE_NOT_ALLOWED_TITLE || 'Duplication Not Allowed',
                                content: Localization.strings.DUPLICATE_NOT_ALLOWED_MESSAGE || 'Duplicating resources is not allowed under your current subscription.',
                                confirm: (typeof Config.SUBSCRIPTION_LINK_FLAG === 'function' && Config.SUBSCRIPTION_LINK_FLAG() === true) ? (Localization.strings.OPEN_SUBSCRIPTION_PAGE || 'Open Subscription Page') : (Localization.strings.CONFIRM || 'Confirm'),
                                cancel: Localization.strings.CLOSE || 'Close',
                            },
                            () => {
                                if (typeof Config.SUBSCRIPTION_LINK_FLAG === 'function' && Config.SUBSCRIPTION_LINK_FLAG() === true) {
                                    window.open(link, '_blank', 'noopener,noreferrer');
                                }
                            },
                            () => {}
                        );
                    } else {
                        this.props.root.addNotification(Localization.strings.DUPLICATE_NOT_ALLOWED_MESSAGE || 'Duplicating resources is not allowed under your current subscription.', false);
                    }
                    return;
                }

                const requestObj = {
                    school_id: this.props.root.getMySchoolId()
                };
                this.pendingDuplicateEpack = epack;
                this.props.root.refs.API.request(
                    "get_epack_quota",
                    requestObj,
                    this.getEpackQuotaForDuplicateOK,
                    this.getEpackQuotaForDuplicateFailed
                );
                return;
            }
        } catch (e) {
            console.error("handleDuplicateClick quota check failed", e);
            // Fail closed when enforcement is on: don't open the duplicate dialog
            // and bypass the quota check just because the check itself threw.
            if (enforceSubscription) {
                this.props.root.addNotification(
                    Localization.strings.LOCALE_QUOTA_CHECK_FAILED
                        || "Could not check the LOCALE quota. Please try again.",
                    false,
                );
                return;
            }
        }

        this.openDuplicateDialog(epack);
    }

    openDuplicateDialog(epack, rawQuota = null) {
        // Defaults to null so the flag-off fall-through below cannot carry a
        // stale figure in; the quota-checked path passes the fresh one.
        this.setState({
            dialogOpen: true,
            selectedEpack: epack,
            newEpackName: `${epack.name} (Copy)`,
            epackQuota: rawQuota,
        });
    }

    getEpackQuotaForDuplicateOK(res) {
        const epack = this.pendingDuplicateEpack;
        this.pendingDuplicateEpack = null;
        // `rawQuota` is what the notice may print, `availableQuota` is the gate.
        // The gate keeps its `|| 0` so a failed parse refuses; the notice must
        // see that same failure as "unknown", not as a confident 0.
        const rawQuota = res.body?.result?.epack_quotas;
        const availableQuota = parseInt(rawQuota, 10) || 0;

        if (availableQuota <= 0) {
            if (this.props.root && this.props.root.refs && this.props.root.refs.confirmDialog) {
                const subscriptionLinkEnabled = (typeof Config.SUBSCRIPTION_LINK_FLAG === 'function' && Config.SUBSCRIPTION_LINK_FLAG() === true);
                const subscriptionUrl = (typeof Config.SUBSCRIPTION_MANAGE_URL === 'function')
                    ? Config.SUBSCRIPTION_MANAGE_URL()
                    : (window.location.origin + '/');
                this.props.root.refs.confirmDialog.show(
                    {
                        title: Localization.strings.LOCALE_LIMIT_REACHED,
                        content: Localization.strings.LOCALE_LIMIT_MESSAGE,
                        confirm: subscriptionLinkEnabled ? Localization.strings.OPEN_SUBSCRIPTION_PAGE : Localization.strings.CONFIRM,
                        cancel: Localization.strings.CLOSE,
                    },
                    () => {
                        if (subscriptionLinkEnabled) {
                            window.open(subscriptionUrl, '_blank', 'noopener,noreferrer');
                        }
                    },
                    () => {}
                );
            } else {
                this.props.root.addNotification(Localization.strings.LOCALE_LIMIT_MESSAGE, false);
            }
            return;
        }

        if (epack) {
            this.openDuplicateDialog(
                epack,
                this.props.root.isLocaleQuotaExemptViewer() ? null : rawQuota,
            );
        }
    }

    getEpackQuotaForDuplicateFailed(err) {
        this.pendingDuplicateEpack = null;
        const key = serverErrorKey(err);
        console.error("getEpackQuotaForDuplicateFailed", failureRemark(err) || err);
        this.props.root.addNotification(
            (key && Localization.strings[key])
                || Localization.strings.LOCALE_QUOTA_CHECK_FAILED
                || "Could not check the LOCALE quota. Please try again.",
            false,
        );
    }

    handleDialogClose() {
        this.setState({
            dialogOpen: false,
            selectedEpack: null,
            newEpackName: '',
            epackQuota: null,
        });
    }

    handleConfirmDuplicate() {
        this.setState({ isDuplicating: true });
        const { selectedEpack, newEpackName } = this.state;
        const requestObj = {
            epack_id: selectedEpack.id,
            user_id: this.props.root.getMyUserId(),
            school_id: this.props.root.getMySchoolId(),
            new_name: newEpackName,
        };
        this.props.root.refs.API.request(
            "duplicate_epack",
            requestObj,
            (res) => this.duplicateSuccess(res, newEpackName),
            this.duplicateFail
        );
    }

    duplicateSuccess(res, originalNewName) {
        this.setState({ isDuplicating: false, dialogOpen: false });
        const result = (res && res.body && res.body.result) || {};
        // No `else`: apiRoutes' duplicate_epack envelope stamps `success: true`
        // onto every 2xx, and the route signals failure with a non-2xx that
        // lands in duplicateFail. A failure branch here could only ever have
        // printed "Duplication failed: undefined".
        if (result.success) {
            const newEpackId = result.new_epack_id;
            const template = Localization.strings.DUPLICATE_SUCCESS;
            this.props.root.addNotification(
                template
                    ? String(template).replace('{0}', String(newEpackId))
                    : `Successfully duplicated as LOCALE ID: ${newEpackId}.`,
            );
            this.fetchDuplicatableEpacks();
            if (this.props.onResourceDuplicated) {
                this.props.onResourceDuplicated();
            }
            this.props.parent.changeView(0);
        }
    }

    duplicateFail(err) {
        const key = serverErrorKey(err, 'duplicate');
        console.error("Duplication API call failed:", failureRemark(err) || err);
        // This dialog stays OPEN on failure, so the figure goes with the write
        // that did not happen — same reasoning as CreateLocaleDialog's
        // createLocaleFail. The notice falls back to its numberless sentence.
        this.setState({ isDuplicating: false, epackQuota: null });
        this.props.root.addNotification(
            (key && Localization.strings[key])
                || Localization.strings.DUPLICATE_FAILED
                || "Duplication failed.",
            false,
        );
    }

    renderEpackTable(title, epacks) {
        return (
            <>
                <Typography variant="h5" style={styles.sectionTitle}>
                    {title} ({epacks.length})
                </Typography>
                {epacks.length === 0 ? (
                    <Typography style={styles.placeholder}>{Localization.strings.NO_RESOURCES_IN_CATEGORY}</Typography>
                ) : (
                    <TableContainer component={Paper} style={styles.tableContainer}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ width: '5%' }}></TableCell>
                                    <TableCell style={{ width: '15%' }}></TableCell>
                                    <TableCell>{Localization.strings.NAME}</TableCell>
                                    <TableCell>{Localization.strings.AUTHOR}</TableCell>
                                    <TableCell>{Localization.strings.SCHOOL}</TableCell>
                                    <TableCell>{Localization.strings.LAST_UPDATE}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {epacks.map((epack) => (
                                    <TableRow key={epack.id} style={{ height: '100px' }}>
                                        <TableCell>
                                            <Tooltip title={Localization.strings.DUPLICATE}>
                                                <IconButton onClick={() => this.handleDuplicateClick(epack)} color="primary">
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <img
                                                src={Config.UPLOAD_PATH() + epack.tn_path}
                                                alt={epack.name}
                                                style={styles.thumbnail}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body1">{epack.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {(epack.description || '').substring(0, 100)}{(epack.description || '').length > 100 ? '...' : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{epack.display_name}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{epack.school_name_eng || epack.school_name}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{new Date(epack.date_update).toLocaleDateString()}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </>
        );
    }

    render() {
        const { isLoading, myEpacks, schoolEpacks, otherEpacks, dialogOpen, selectedEpack, newEpackName, isDuplicating } = this.state;

        if (isLoading) {
            return (
                <Box style={styles.pageContainer} display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 128px)">
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <div style={styles.pageContainer}>
                <Typography variant="h4" gutterBottom>
                    {Localization.strings.DUPLICATABLE_LOCALE_TITLE}
                </Typography>
                
                {this.renderEpackTable(Localization.strings.MY_LOCALE, myEpacks)}
                {this.renderEpackTable(Localization.strings.SCHOOL_LOCALE_DUPLICATABLE, schoolEpacks)}
                {this.renderEpackTable(Localization.strings.OTHER_LOCALE_DUPLICATABLE, otherEpacks)}

                <Dialog open={dialogOpen} onClose={this.handleDialogClose} maxWidth="sm" fullWidth>
                    <DialogTitle>{Localization.strings.CONFIRM_DUPLICATION}</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {Localization.strings.formatString(Localization.strings.DUPLICATION_CONFIRM_MESSAGE, <b>{selectedEpack?.name}</b>)}
                        </Typography>
                        {/* Duplicating makes a NEW locale, so it redeems a quota
                            unit exactly as creating one does — same reminder,
                            same audience. See tools/localeQuotaNotice.js. */}
                        {this.props.root && typeof this.props.root.shouldWarnLocaleQuota === 'function'
                            && this.props.root.shouldWarnLocaleQuota() && (
                            <Typography color="error" style={{ fontWeight: 500, marginTop: '8px' }}>
                                {localeQuotaNoticeText(
                                    Localization.strings.LOCALE_QUOTA_DEDUCT_DUPLICATE_NOTICE,
                                    Localization.strings.LOCALE_QUOTA_DEDUCT_DUPLICATE_NOTICE_WITH_QUOTA,
                                    this.state.epackQuota,
                                )}
                            </Typography>
                        )}
                        <TextField
                            autoFocus
                            margin="dense"
                            label={Localization.strings.NEW_LOCALE_NAME}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={newEpackName}
                            onChange={(e) => this.setState({ newEpackName: e.target.value })}
                            helperText={Localization.strings.DUPLICATION_NAME_HINT}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleDialogClose}>{Localization.strings.CANCEL}</Button>
                        <Button onClick={this.handleConfirmDuplicate} disabled={isDuplicating}>
                            {isDuplicating ? <CircularProgress size={24} /> : Localization.strings.CONFIRM}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

export default ResourceLibraryPage; 